import { Stack } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { SearchResult } from 'app/concierge/types';
import { conciergeAPI, IReferenceData, ITileInfoData } from 'api/concierge';
import { openInNewTab } from 'tools/tools';
import { useNavigate } from 'react-router-dom';
import SimpleResultItem from './types/SimpleResultItem';
import ReferenceResultItem from './types/ReferenceResultItem';
import ElementResultItem from './types/ElementResultItem';
import DatasetDataResultItem from './types/DatasetDataResultItem';
import AccessDeniedPopup from 'components/common/popup/AccessDeniedPopup';
import { AccessDeniedTileInfo } from 'app/home/index.functions';
import { useSelector } from 'react-redux';
import { dispatchCustomEvent } from 'tools/events';
import { merge } from 'lodash';
import { ICustomStyles } from 'app/concierge/panel';
import defaultStyles from './styles';

interface IProps {
    items: SearchResult[];
    useInfoButton?: boolean;
}

export default function ResultItems({ items, useInfoButton }: IProps) {
    const navigate = useNavigate();

    const [tilesInfo, setTilesInfo] = useState<ITileInfoData[]>([]);
    const [referencesData, setReferencesData] = useState<IReferenceData[]>([]);
    const [accessDeniedPopupTile, setAccessDeniedPopupTile] = useState<AccessDeniedTileInfo | null>(null);

    const isAppPanel = useSelector((state: any) => state.concierge.isAppPanel) as boolean;
    const customStyles = useSelector((state: any) => state.concierge.customStyles) as ICustomStyles;

    const styles = useMemo(() => {
        return merge({}, defaultStyles, customStyles?.activeChatMessageResultItem) as typeof defaultStyles;
    }, [customStyles]);

    useEffect(() => {
        const ids = items
            .filter((item) => item.type === 'dashboard_element')
            .map((item) => `${item.id}_${item.second_id}`);

        if (ids.length > 0) {
            conciergeAPI.getTilesInfo(ids).then((response) => {
                setTilesInfo(response);
            });
        } else {
            setTilesInfo([]);
        }

        const referenceIds = items
            .filter((item) => item.type === 'external_report_reference')
            .map((item) => String(item.id));

        if (referenceIds.length > 0) {
            conciergeAPI.getExternalReportReferenceInfo(referenceIds).then((response) => {
                setReferencesData(response);
            });
        } else {
            setReferencesData([]);
        }
    }, [items]);

    const onItemClick = (result: SearchResult) => {
        if (isAppPanel) {
            dispatchCustomEvent('mi-concierge-panel', {
                action: 'item-click',
                payload: {
                    item: result,
                },
            });
        } else {
            switch (result.type) {
                case 'portal_page':
                    if (result.url) {
                        openInNewTab(result.url);
                    }

                    break;
                case 'notification_schedule_distribution':
                    navigate(`/notification/burst/${result.id}`);
                    break;
                case 'dataset':
                    navigate(`/dataset/${result.id}`);
                    break;
                case 'dataset_data':
                    if (result.external_link) {
                        openInNewTab(result.external_link);
                    }
                    break;
                case 'document':
                    if (result.external_link) {
                        openInNewTab(result.external_link);
                    } else {
                        navigate(`/content/index/view-document/document/${result.id}`);
                    }
                    break;
            }
        }
    };

    return (
        <>
            <Stack sx={styles.resultItems}>
                {items.map((result, index) => {
                    if (result.type === 'dashboard_element') {
                        const tileInfo = tilesInfo.find(
                            (info) => info.element_id == result.id && info.segment_value_id == result.second_id,
                        );

                        if (!tileInfo) {
                            return null;
                        }

                        return (
                            <ElementResultItem
                                result={result}
                                key={index}
                                tileInfo={tileInfo}
                                setAccessDeniedPopupTile={setAccessDeniedPopupTile}
                                useInfoButton={useInfoButton}
                            />
                        );
                    }

                    if (result.type === 'external_report_reference') {
                        return <ReferenceResultItem result={result} key={index} referencesData={referencesData} />;
                    }

                    if (result.type === 'dataset_data') {
                        return <DatasetDataResultItem result={result} key={index} />;
                    }

                    return <SimpleResultItem result={result} key={index} onItemClick={onItemClick} />;
                })}
            </Stack>
            {accessDeniedPopupTile && (
                <AccessDeniedPopup
                    accessDeniedTile={accessDeniedPopupTile}
                    setAccessDeniedPopupTile={setAccessDeniedPopupTile}
                />
            )}
        </>
    );
}
